import { NgStyle } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ACCOUNTTYPE,
  AccountRequirements,
  BeneficiaryBankRequirements,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GetBeneficiaryRequiredFieldsAction } from '@app/finxone-web-frontend/app/lib/actions/beneficiary.action';
import { BeneficiaryState } from '@app/finxone-web-frontend/app/lib/state/beneficiary.state';
import {
  FormActionState,
  FormActionType,
  FormDataType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { kebabToCamelCase } from '@app/finxone-web-frontend/app/lib/utils/utils';
import {
  nameRegex,
  noNumbersValidator,
} from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import { Select, Store } from '@ngxs/store';

import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { mapFormToFormWithDestinationIdentifier } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/beneficiary-zone/submit-edit-beneficiary-request.utils';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { UiComponentsModule, ValidationConfig } from '@finxone-platform/ui-components';
import { Observable, Subject, debounceTime, map, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  selector: 'finxone-web-frontend-add-edit-beneficiary-intl-widget',
  templateUrl: './add-edit-beneficiary-intl-widget.component.html',
  imports: [NgStyle, ReactiveFormsModule, UiComponentsModule],
})
export class AddEditBeneficiaryIntlWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public addressRequired = '';
  public recipientName = 'name';
  public firstName = 'firstName';
  public lastName = 'lastName';
  public beneficiaryType = '';

  public addBeneficiaryIntlForm: FormGroup;
  private formStateValues: Record<string, string> = {};
  public enableBeneficiaryFirstAndLastName = false;
  public requirements!: BeneficiaryBankRequirements;
  public filteredRequirements: AccountRequirements = {};

  public countryCode = 'GB';
  public currencyCode = 'GBP';
  public isSubmitTypeAsynchronous = false;

  @Select(FormActionState.getFormActionStateWithId(FormActionTypeEnum.INTL_BENEFICIARY_ADD))
  formAction!: Observable<FormActionType>;

  public fieldsList: Array<{
    label: string;
    title: string;
    item: { regex?: RegExp; requirement: string };
    validated?: boolean;
  }> = [];
  public fieldsValidationConfig: { [key: string]: ValidationConfig[] } = {};
  private isValueChanged = false;
  public nextBtnFailed = false;
  public isEditIntlBeneficiary = false;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private formSubmissionService: FormSubmissionService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private formBuilder: FormBuilder,
    private store: Store,
  ) {
    super();
    this.addBeneficiaryIntlForm = this.formBuilder.group({});
  }
  ngOnInit(): void {
    this.beneficiaryType = this.dataIn.attri.widgetProperties['beneficiaryType'];
    this.isEditIntlBeneficiary = this.dataIn?.widgetConfig?.name === WidgetNames.EDIT_BENEFICIARY_INTL_WIDGET;
    this.setupRequirementsBasedOnCountryCurrencyPair();
  }

  private setupRequirementsBasedOnCountryCurrencyPair() {
    this.enableBeneficiaryFirstAndLastName = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    )?.enableBeneficiaryFirstAndLastName;

    this.formAction.pipe(takeUntil(this.destroy$)).subscribe((formValues: FormActionType) => {
      this.formStateValues = this.enableBeneficiaryFirstAndLastName
        ? this.extractNameParts(formValues?.formData)
        : formValues?.formData;

      const { countryIso2, currency } = this.formStateValues || {};
      this.countryCode = countryIso2 || 'GB';
      this.currencyCode = currency || 'GBP';

      if (this.isEditIntlBeneficiary) {
        this.beneficiaryType = this.formStateValues?.['accountType'] || this.beneficiaryType;
      }

      if (Object.keys(this.formStateValues).length && !this.requirements) {
        this.getRequiredFields();
      }
    });
  }

  /**
   * Extracts first and last name from a full name string.
   */
  private extractNameParts(formData: FormDataType) {
    const name = formData?.['name']?.trim();
    if (!name) return formData;

    const nameParts = name.split(' ');
    const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0];
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

    return { ...formData, firstName, lastName };
  }

  /**
   * Checks if the form context matches the beneficiary type.
   */
  private get isFormContextSame(): boolean {
    const accountType = this.formStateValues?.['accountType'];
    return (this.beneficiaryType || this.dataIn.attri.widgetProperties['beneficiaryType']) === accountType;
  }

  private getRequiredFields() {
    this.store.dispatch(new GetBeneficiaryRequiredFieldsAction(this.countryCode, this.currencyCode));

    this.store
      .select(BeneficiaryState.getBeneficiaryRequiredFields())
      .pipe(
        takeUntil(this.destroy$),
        map((data) => {
          const requirements = data?.beneficiaryBankAccount;
          if (!requirements) return;

          // Reset fields and filter requirements
          this.fieldsList = [];
          this.filteredRequirements = {};
          this.requirements = requirements;
          this.filterNonRequiredFields(this.requirements);

          // Build fieldsList efficiently
          Object.entries(this.filteredRequirements).forEach(([key, value]) => {
            const widgetKey = Object.keys(this.widgetProperties).find(
              (widget) => kebabToCamelCase(widget) === key,
            );

            if (widgetKey && !this.fieldsList.some((field) => field.title === key)) {
              this.fieldsList.push({
                label: this.widgetProperties[widgetKey],
                title: key,
                item: value,
              });
            }
          });

          this.generateForm();
        }),
      )
      .subscribe();
  }

  private generateForm() {
    this.addBeneficiaryIntlForm = this.formBuilder.group({});
    const controlNames = Object.keys(this.addBeneficiaryIntlForm.controls);
    if (this.fieldsList.length > 0) {
      controlNames.forEach((name) => this.addBeneficiaryIntlForm.removeControl(name));
      for (const field of this.fieldsList) {
        const validators = [];
        const validatorConfigForInput = [];
        if (field.item.requirement === 'required') {
          validators.push(Validators.required);
          validatorConfigForInput.push({
            type: 'required',
            message: '',
          });
        }
        if (field.item.regex) {
          validators.push(Validators.pattern(field.item.regex));
          validatorConfigForInput.push({
            type: 'pattern',
            message: 'Entered value is not in correct format',
            params: field.item.regex,
          });
        }
        if (
          (field.title === 'lastName' || field.title === 'firstName' || field.title === 'name') &&
          this.beneficiaryType === ACCOUNTTYPE.PERSONAL
        ) {
          validators.push(noNumbersValidator());
          validatorConfigForInput.push({
            type: 'custom',
            message: 'Only alphabetic characters are allowed',
            validatorFn: noNumbersValidator(),
          });
        }
        this.addBeneficiaryIntlForm.addControl(
          field.title,
          new FormControl(this.isFormContextSame ? this.formStateValues[field.title] : '', validators),
        );
        this.fieldsValidationConfig[field.title] = validatorConfigForInput;
      }

      this.updateFormStatus();
      this.subscribeToFormChangesAndEmitValues();
    }
  }

  private subscribeToFormChangesAndEmitValues() {
    this.addBeneficiaryIntlForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(600),
        map(() => {
          if (this.isSubmitTypeAsynchronous) {
            const formObject = this.addBeneficiaryIntlForm.value;
            if ('firstName' in formObject && 'lastName' in formObject) {
              const firstName = formObject['firstName'];
              const lastName = formObject['lastName'];
              const fullName = firstName + ' ' + lastName;
              // Remove firstName and lastName from formObject
              delete formObject['firstName'];
              delete formObject['lastName'];
              // Add fullName under name key
              formObject['name'] = fullName;
            }

            if (this.addBeneficiaryIntlForm.valid || this.isValueChanged) {
              this.isValueChanged = false;
            }
          }

          this.updateFormStatus();
        }),
      )
      .subscribe();
  }

  private filterNonRequiredFields(required: object) {
    if (
      (this.beneficiaryType || this.dataIn.attri.widgetProperties['beneficiaryType']) === ACCOUNTTYPE.PERSONAL
    ) {
      if (!this.enableBeneficiaryFirstAndLastName) {
        this.filteredRequirements = {
          ...this.filteredRequirements,
          [this.recipientName]: {
            requirement: 'required',
            regex: nameRegex,
          },
        };
      } else {
        this.filteredRequirements = {
          ...this.filteredRequirements,
          [this.firstName]: {
            requirement: 'required',
            regex: nameRegex,
          },
          [this.lastName]: {
            requirement: 'required',
            regex: nameRegex,
          },
        };
      }
    } else {
      this.filteredRequirements = {
        ...this.filteredRequirements,
        [this.recipientName]: {
          requirement: 'required',
        },
      };
    }

    for (const [key, value] of Object.entries(required)) {
      //routingCodes is nested so get each entry from routingCodes
      if (key === 'routingCodes') {
        for (const [k, v] of Object.entries(value)) {
          const formattedKey = kebabToCamelCase(k);
          this.filteredRequirements = {
            ...this.filteredRequirements,
            [formattedKey]: v,
          };
        }
      } else if (key === 'requirements') {
        this.addressRequired = value['beneficiaryAddress'];
      } else if (value['requirement'] !== 'none') {
        this.filteredRequirements = {
          ...this.filteredRequirements,
          [key]: value,
        };
      }
    }
  }

  get widgetProperties() {
    return this.dataIn.attri.widgetProperties;
  }

  private createFormObject() {
    let formObject: any = {
      beneficiaryAddress: this.addressRequired,
      accountType: this.beneficiaryType,
    };
    for (const [key, value] of Object.entries(this.addBeneficiaryIntlForm.value)) {
      if (key === 'Business Name') {
        const nameKey = 'fullName';
        formObject = { ...formObject, [nameKey]: value };
      } else {
        formObject = { ...formObject, [key]: value };
      }
    }
    return formObject;
  }

  public valueChangeTrigger() {
    this.isValueChanged = true;
    this.nextBtnFailed = false;
  }

  private updateFormStatus() {
    const formObject = this.createFormObject();

    if ('firstName' in formObject && 'lastName' in formObject) {
      const firstName = formObject['firstName'];
      const lastName = formObject['lastName'];
      const fullName = firstName + ' ' + lastName;
      // Remove firstName and lastName from formObject
      delete formObject['firstName'];
      delete formObject['lastName'];
      // Add fullName under name key
      formObject['name'] = fullName;
    }

    if (this.addBeneficiaryIntlForm.valid) {
      const intlBeneficiaryFormData = {
        ...formObject,
        isCredentialsFormValid: this.addBeneficiaryIntlForm.valid,
      };
      this.formSubmissionService.storeFormData(
        this.isEditIntlBeneficiary
          ? mapFormToFormWithDestinationIdentifier(intlBeneficiaryFormData)
          : intlBeneficiaryFormData,
        FormActionTypeEnum.INTL_BENEFICIARY_ADD,
      );
    } else {
      if (!this.isFormContextSame || this.addBeneficiaryIntlForm.invalid) {
        const intlBeneficiaryFormData = {
          isCredentialsFormValid: this.addBeneficiaryIntlForm.valid,
        };
        this.formSubmissionService.storeFormData(
          intlBeneficiaryFormData,
          FormActionTypeEnum.INTL_BENEFICIARY_ADD,
        );
      }
      this.addBeneficiaryIntlForm.markAllAsTouched();
    }
  }
}
